import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertTriangle, Loader2, Activity } from 'lucide-react';
import axios from 'axios';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Cookies', href: '/cookies' },
  { label: 'Help & Legal', href: '/help-legal' },
  { label: 'Trust Center', href: '/trust' },
];

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/status`, { timeout: 8000 });
      setData(res.data);
    } catch (e) {
      setError('Unable to reach the API right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, []);

  const overallOk = data?.overall === 'operational';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="status-page">
      {/* Header */}
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" variant="light" linkTo="/" />
          <Link
            to="/trust"
            className="inline-flex items-center gap-1 text-[12.5px] text-white/70 hover:text-white transition-colors"
            data-testid="status-back-trust"
          >
            Trust Center <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#DA291C] mb-3">System Status</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            ShiftCover365 status
          </h1>
          <p className="mt-3 text-[14.5px] text-slate-600 max-w-2xl leading-relaxed">
            Live operational state of the platform and its core dependencies. Auto-refreshes every 30 seconds.
          </p>

          {/* Overall card */}
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5" data-testid="status-overall-card">
            <div className="flex items-center gap-4">
              <OverallIcon loading={loading} ok={overallOk} error={!!error} />
              <div>
                <p className="text-[17px] sm:text-lg font-bold text-slate-900 tracking-tight">
                  {loading ? 'Checking system…' : error ? 'Status unavailable' : overallOk ? 'All systems operational' : 'Some services degraded'}
                </p>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {error || (data?.checkedAt ? `Last checked ${new Date(data.checkedAt).toLocaleString('en-GB')}` : 'Establishing connection…')}
                </p>
              </div>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 px-4 h-10 text-[12.5px] font-semibold transition-colors disabled:opacity-60 shrink-0"
              data-testid="status-refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
              Refresh
            </button>
          </div>

          {/* Components */}
          <h2 className="mt-12 mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Components
          </h2>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {(data?.components || [
              { name: 'API' }, { name: 'Database' }, { name: 'Email' }, { name: 'Payments' },
            ]).map((c) => (
              <div
                key={c.name}
                className="px-5 sm:px-6 py-4 flex items-center justify-between"
                data-testid={`status-component-${c.name.toLowerCase()}`}
              >
                <span className="text-[14px] font-semibold text-slate-900">{c.name}</span>
                <StatusBadge status={c.status} loading={loading} />
              </div>
            ))}
          </div>

          {/* Active incidents */}
          {data?.activeIncidents && data.activeIncidents.length > 0 && (
            <>
              <h2 className="mt-12 mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                Active incidents
              </h2>
              <div className="space-y-3" data-testid="status-active-incidents">
                {data.activeIncidents.map((inc) => (
                  <IncidentCard key={inc.id} incident={inc} active />
                ))}
              </div>
            </>
          )}

          {/* Incident history */}
          <h2 className="mt-12 mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            Incident history
          </h2>
          {(() => {
            const past = (data?.incidents || []).filter(
              (i) => !(data?.activeIncidents || []).some((a) => a.id === i.id)
            );
            if (loading) {
              return (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 flex items-center justify-center text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading history…
                </div>
              );
            }
            if (past.length === 0) {
              return (
                <div
                  className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-white p-8 sm:p-10 text-center"
                  data-testid="status-incidents-empty"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 mb-3">
                    <Activity className="h-5 w-5 text-emerald-600" />
                  </span>
                  <p className="text-[15px] font-bold text-slate-900">No past incidents recorded</p>
                  <p className="mt-1.5 text-[13px] text-slate-500 max-w-md mx-auto leading-relaxed">
                    The platform has been running smoothly. Any incidents published by our team will appear here with full update timelines.
                  </p>
                </div>
              );
            }
            return (
              <div className="space-y-3" data-testid="status-incident-history">
                {past.map((inc) => (
                  <IncidentCard key={inc.id} incident={inc} />
                ))}
              </div>
            );
          })()}

          {/* Footnote */}
          <p className="mt-10 text-[12.5px] text-slate-500 leading-relaxed">
            This page is generated live from a public health endpoint and does not currently use a third-party uptime provider.
            Past incidents are recorded by our team and shown above.
          </p>
        </div>
      </main>

      {/* Footer legal nav */}
      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[12.5px]">
            {LEGAL_LINKS.map((l, i) => (
              <span key={l.href} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-slate-300">|</span>}
                <Link to={l.href} className="text-slate-500 hover:text-slate-900 transition-colors">
                  {l.label}
                </Link>
              </span>
            ))}
          </nav>
          <p className="text-[11.5px] text-slate-400">© {new Date().getFullYear()} ShiftCover365 · All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function OverallIcon({ loading, ok, error }) {
  if (loading) {
    return (
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 border border-slate-200 shrink-0">
        <Loader2 className="h-5 w-5 text-slate-500 animate-spin" />
      </span>
    );
  }
  if (error || !ok) {
    return (
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-50 border border-amber-200 shrink-0">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
      </span>
    );
  }
  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 shrink-0">
      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
    </span>
  );
}

function StatusBadge({ status, loading }) {
  if (loading || !status) {
    return <span className="inline-flex items-center gap-2 text-[12px] text-slate-400">Checking…</span>;
  }
  if (status === 'operational') {
    return (
      <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-emerald-700">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        Operational
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-amber-700">
      <span className="h-2 w-2 rounded-full bg-amber-500" />
      Degraded
    </span>
  );
}

function IncidentCard({ incident, active = false }) {
  const sevCls = {
    minor:    'bg-amber-50 text-amber-700 border-amber-100',
    major:    'bg-orange-50 text-orange-700 border-orange-100',
    critical: 'bg-red-50 text-red-700 border-red-100',
  }[(incident.severity || 'minor').toLowerCase()] || 'bg-slate-50 text-slate-700 border-slate-200';
  const statusCls = active
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-100';
  const startedAt = incident.startedAt ? new Date(incident.startedAt).toLocaleString('en-GB') : '';
  const resolvedAt = incident.resolvedAt ? new Date(incident.resolvedAt).toLocaleString('en-GB') : null;
  const updates = incident.updates || [];

  return (
    <div
      className={`rounded-2xl border bg-white overflow-hidden ${active ? 'border-amber-300 shadow-[0_4px_16px_-8px_rgba(245,158,11,0.4)]' : 'border-slate-200'}`}
      data-testid={`incident-${incident.id}`}
    >
      <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full border text-[10.5px] font-semibold uppercase tracking-[0.08em] px-2 h-5 ${sevCls}`}>
              {incident.severity || 'minor'}
            </span>
            <span className={`inline-flex items-center rounded-full border text-[10.5px] font-semibold uppercase tracking-[0.08em] px-2 h-5 ${statusCls}`}>
              {incident.status || 'resolved'}
            </span>
            {(incident.affectedComponents || []).map((c) => (
              <span key={c} className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-medium px-2 h-5">{c}</span>
            ))}
          </div>
          <h3 className="mt-2 text-[14.5px] font-bold text-slate-900 leading-snug">{incident.title}</h3>
        </div>
        <div className="text-[11.5px] text-slate-500 sm:text-right">
          <div>Started {startedAt}</div>
          {resolvedAt && <div className="text-emerald-700 font-medium">Resolved {resolvedAt}</div>}
        </div>
      </div>

      {updates.length > 0 && (
        <ol className="px-5 sm:px-6 py-4 space-y-3">
          {updates.slice().reverse().map((u) => (
            <li key={u.id} className="flex gap-3">
              <span className="mt-1.5 h-2 w-2 rounded-full bg-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {u.status} · {u.createdAt ? new Date(u.createdAt).toLocaleString('en-GB') : ''}
                </div>
                <p className="mt-0.5 text-[13.5px] text-slate-800 leading-relaxed">{u.message}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
